import {nanoid} from 'nanoid';
import delay from 'delay';
import {isNil, pick, isEmpty, omit, isObject, isArray, noop, assign} from 'lodash';
import axios from 'axios';
import qs from 'qs';

const requiredInitialFields = ['userid'];
const selfFetchOptionKeys = ['responseErrorsHandler'];

export default class HairuoRequest {
    constructor({
        baseURL = '',
        apiPath = '/hairuo/request.ajax',
        userid,
        optid,
        token,
        debug,
        genReqId = nanoid,
        onredirect = noop,
        onerror = noop,
        n8nLog = noop,
        requestBodySerializer = qs.stringify
    }) {
        this.baseURL = baseURL;
        this.apiPath = apiPath;
        // 如果路由没有传递userid,有optid的话则填充optid，表示操作本人账号
        this.userid = userid || optid || -1;
        this.optid = optid || -1;
        this.token = token;
        this.generateRequestID = genReqId;
        this.redirectCallback = onredirect;
        this.onerror = onerror;
        this.debug = debug;
        this.requestBodySerializer = requestBodySerializer;
        this.n8nLog = n8nLog;

        let missingRequiredFields = requiredInitialFields.filter(key => isNil(this[key]));
        if (missingRequiredFields.length) {
            throw new Error('Fail to initialize hairuo request. Missing required field(s): '
                + missingRequiredFields.join(', '));
        }

        this.request = axios.create({
            baseURL: this.baseURL,
            timeout: 15e4,
            withCredentials: true
        });
    }

    throwError(err) {
        setTimeout(() => this.onerror(err), 0);
        throw err;
    }

    // 接口异常监控
    // error 字段与 axios 的 error 字段一致，至少有 {message, config, request, response}
    // requestParams 必须有字段： {userid, reqid, path, params}
    sendApiErrorLog() {
        // setTimeout(() => {
        //     captureAPIException(error, requestParams);
        // }, 0);
    }

    async fetch(path, params = {}, options = {}) {
        let {responseErrorsHandler} = options;
        let requestData = {
            ...pick(this, [...requiredInitialFields, 'optid', 'token', 'debug']),
            reqid: this.generateRequestID(),
            path,
            params: JSON.stringify(params)
        };
        const requestParams = {...requestData, params};

        let result;
        const apiPath = this.apiPath + `?path=${encodeURIComponent(path)}`;
        try {
            result = await this.request.post(
                apiPath,
                this.requestBodySerializer(requestData),
                omit(options, selfFetchOptionKeys)
            );
            // 部分接口才发打点日志
            try {
                if (!path.includes('query') && !path.includes('get')
                    && JSON.stringify(params) !== '{}'
                ) {
                    this.n8nLog({
                        type: 'send_request',
                        path,
                        reqid: requestData.reqid,
                        params: JSON.stringify(params)
                    });
                }
            }
            catch (e) {}
        }
        catch (error) {
            if (options.noErrorTip === true) {
                return {};
            }
            this.processFetchError(error, requestParams);
        }

        return this.processHairuoResult(result, requestParams, responseErrorsHandler, params.isBasic).data;
    }

    processHairuoResult(result, requestParams, responseErrorsHandler = defaultResponseErrorsHandler, isBasic = false) {
        const errorInfo = {
            config: result.config,
            response: result,
            request: result.request
        };

        result = result.data; // axios response.data

        if (result.redirect) {
            // 没权限
            if (result.status === 403 || result.redirecturl.indexOf('noauth') > 0) {
                if (!isBasic) {
                    return result;
                }
                this.throwError(createError({message: '无操作权限', code: '-3'}, errorInfo));
            }

            // 没登录
            this.redirectCallback(result);
            throw createError({message: '未取到登录信息', code: '-2'}, errorInfo);
        }
        else if (result.status === 2) {
            // 没登录
            this.redirectCallback(result);
            let message;
            try {
                message = responseErrorsHandler(result.errors, result);
            }
            catch (err) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('[HAIRUO] Fail to execute error handler', err);
                }
            }
            throw createError(message || {message: '未取到登录信息', code: '-2'}, errorInfo);
        }
        else if (result.status !== 0) {
            let message;
            try {
                message = responseErrorsHandler(result.errors, result);
            }
            catch (err) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('[HAIRUO] Fail to execute error handler', err);
                }
            }

            const error = createError(message || '系统异常', errorInfo);
            // 接口500时上报Api异常，正常的业务报错不需要上报
            // 接口错误情况与status返回对应关系见: http://wiki.baidu.com/pages/viewpage.action?pageId=687695568
            if (result.status === 3) {
                this.sendApiErrorLog(error, requestParams);
            }
            this.throwError(error);
        }
        else if (isEmpty(result)) {
            const error = createError('返回空数据', errorInfo);
            this.sendApiErrorLog(error, requestParams);
            this.throwError(error);
        }

        return result;
    }

    async roll(path, params, {interval, timeout, onProgress = noop, cancelToken, until}) {
        const timeoutError = new Error('等待结果超时！');
        const startTime = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
            onProgress(0);
            try {
                const response = await this.fetch(path, params, {cancelToken});
                if (until(response)) {
                    return response;
                }
                const passTime = Date.now() - startTime;
                // 还剩不到1秒就超时的直接算超时，别等到下一个loop再报
                // 否则就缩短 interval 到剩余的时间再请求一次
                if (passTime + 1000 >= timeout) {
                    throw timeoutError;
                }
                onProgress(passTime / timeout);

                // 传给 fetch 的 cancelToken 用于在网络请求过程中触发取消，透传到axios内部实现的逻辑
                // 这里的 cancelToken.promise 用于在等待时触发取消，不等了
                let delayPromise = delay(Math.min(interval, timeout - passTime));
                await (cancelToken ? Promise.race([delayPromise, cancelToken.promise]) : delayPromise);
            }
            catch (error) {
                if (axios.isCancel(error) || error === timeoutError) {
                    throw error;
                }
                throw new Error('请求错误：' + error.message);
            }
        }
    }

    processFetchError(error, requestParams) {
        if (axios.isCancel(error)) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[HAIRUO] Request canceled', error.message);
            }
            throw error;
        }

        if (process.env.NODE_ENV === 'development') {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log('[HAIRUO]', error.response.data);
                console.log('[HAIRUO]', error.response.status);
                console.log('[HAIRUO]', error.response.headers);
            }
            else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.log('[HAIRUO]', error.request);
            }
            else {
                // Something happened in setting up the request that triggered an Error
                console.log('[HAIRUO] Error', error.message);
            }
            console.log('[HAIRUO]', error.config);
        }

        this.sendApiErrorLog(error, requestParams); // 接口异常监控
        if (error.message?.toString().indexOf('timeout') >= 0) {
            error.message = '超时，请重试';
        }
        else {
            error.message = `请求错误: ${error.message}`;
        }
        error.hairuoCode = '-1';
        this.throwError(error);
    }
}

function defaultResponseErrorsHandler(errors) {
    return errors || '系统异常';
}

function createError(message, errorInfo) {
    let error;
    if (isArray(message)) {
        // 多个错误同时显示，需要都往外暴露，format时才可以处理
        error = createError(message[0], errorInfo);
        error.hairuoErrors = message.map(msg => createError(msg));
    }
    else if (isObject(message)) {
        error = new Error(message.message);
        // 注意区分 axios 错误code
        error.hairuoCode = message.code;
        error.hairuoPosition = message.position;
    }
    else {
        error = new Error(message);
    }
    // 在这里统一修改错误话术，后端话术可读性差
    let msgText = error.message || '系统异常';
    if (msgText.indexOf('backend service error') >= 0) {
        msgText = '系统异常';
    }
    else if (msgText.indexOf('csrf token') >= 0) {
        msgText = '登录失效';
    }
    error.message = msgText;

    if (errorInfo) {
        assign(error, errorInfo);
    }

    return error;
}
